import styled, {css} from "styled-components";
import type {Portfolio} from "../../../data/models/Portfolio";
import type {User} from "../../../data/models/User";
import type {UserGroup} from "../../../data/models/UserGroup";
import {XyiconFeature} from "../../../generated/api/base";
import {InitialsV5, IsRoundClassName} from "../widgets/InitialsV5";
import {baseDistance} from "../styles/styles";
import {NameContainerStyled} from "./SharingPanel.styled";

const addParenthesesMaybe = (text: string, addParentheses: boolean) => {
	if (addParentheses) {
		return text ? `(${text})` : "";
	}

	return text ?? "";
};

type ItemTypeWithInitials = User | UserGroup | Portfolio;

interface IUserOrUserGroupOrPortfolioProps {
	readonly variant: "big" | "small";
	readonly item: ItemTypeWithInitials;
}

export const ItemWithInitials = (props: IUserOrUserGroupOrPortfolioProps) => {
	const {item, variant} = props;
	const {ownFeature} = item;
	const name =
		ownFeature === XyiconFeature.User
			? (item as User).fullName
			: ownFeature === XyiconFeature.UserGroup
				? (item as UserGroup).name
				: (item as Portfolio).name;
	const initialSize = variant === "big" ? 32 : 24;

	return (
		<ItemWithInitialsStyled $inline={variant !== "big"}>
			{ownFeature === XyiconFeature.User ? (
				(item as User).profileFileName ? (
					<img
						src={(item as User).profileFileName}
						alt={`${(item as User).fullName} profile image`}
					/>
				) : (
					<InitialsV5
						size={initialSize}
						name={(item as User).fullName || (item as User).email}
						className={IsRoundClassName}
					/>
				)
			) : (
				<InitialsV5
					name={name}
					size={initialSize}
				/>
			)}
			<NameContainerStyled style={{display: variant === "big" ? "initial" : "block"}}>
				{name && (
					<div
						className="name"
						title={name}
					>
						{name}
					</div>
				)}
				{ownFeature === XyiconFeature.User ? (
					<div className="email">{addParenthesesMaybe((item as User).email, variant === "small" && !!name)}</div>
				) : (
					ownFeature === XyiconFeature.UserGroup && <div className="counter">{(item as UserGroup).renderMemberCount()}</div>
				)}
			</NameContainerStyled>
		</ItemWithInitialsStyled>
	);
};

export const renderItemWithInitials = (item: ItemTypeWithInitials, variant: "big" | "small") => {
	if (item == null && variant === "small") {
		return <></>;
	}

	return (
		<ItemWithInitials
			item={item}
			variant={variant}
		/>
	);
};

const ItemWithInitialsStyled = styled.div<{$inline?: boolean}>`
	display: flex;
	align-items: center;
	min-width: 0;

	${NameContainerStyled} {
		flex-direction: column;
		align-items: flex-start;

		${(props) =>
			props.$inline &&
			css`
				& > div {
					display: inline;

					&:not(:last-child) {
						margin-right: ${baseDistance.sm};
					}
				}
			`}
	}
`;
